import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Plus, Search, SortAsc, Pencil, AlertCircle, Mail } from "lucide-react";
import Loading from "../../component/Loading/Loading";
import AlertMsg from "../../component/AlertMsg/AlertMsg";
import Pagination from "../../utils/Pagenation/Pagenation";
import useAxiosInstance from "../../services/useAxiosInstance";
import GlassmorphicModal from "../../component/ReusableComponents/GlassmorphicModal";
import { AiOutlineSortAscending } from "react-icons/ai";

const ContractList = () => {
  const axiosInstance = useAxiosInstance();
  const employeeIdFromState = useSelector((state) => state.auth.employeeId) || 0;
  const managerIdFromState = useSelector((state) => state.auth.managerId);
  const roleId = useSelector((state) => state.auth.roleId);

  let employeeId = employeeIdFromState;
  let managerId = managerIdFromState;

  if (managerId === null) {
    managerId = employeeId;
    employeeId = 0;
  }

  const [alert, setAlert] = useState({ visible: false, message: '', type: '' });
  const designationId = 1;
  const [contracts, setContracts] = useState([]);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedCommunityId, setSelectedCommunityId] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [salesManagerName, setSalesManagerName] = useState("");
  const [salesManagerId, setSalesManagerId] = useState(managerId);
  const [salesPersonId, setSalesPersonId] = useState(employeeId);
  const [managers, setManagers] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchManagers = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(`/api/public/employee/getEmployeesByDesignation/${designationId}`);
        setManagers(response.data);
      } catch (error) {
        console.error("There was an error fetching the sales managers!", error);
      } finally {
        setLoading(false);
      }
    };

    fetchManagers();
  }, [designationId]);

  const handleManagerChange = (selectedId) => {
    setSalesManagerId(selectedId);

    const selectedManager = managers.find(manager => manager.employeeId === selectedId);
    if (selectedManager) {
      setSalesManagerName(selectedManager.firstName);
    } else {
      setSalesManagerName("");
    }
  };

  useEffect(() => {
    const fetchManagerName = async () => {
      try {
        setLoading(true);
        if (roleId === 1004) {
          const selectedManager = managers.find(manager => manager.employeeId === salesManagerId);
          if (selectedManager) {
            setSalesManagerName(selectedManager.firstName);
          } else {
            setSalesManagerName("");
          }
        }
      } catch (error) {
        console.error("Error fetching sales manager name:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchManagerName();
  }, [roleId, salesManagerId, managers]);

  useEffect(() => {
    const fetchSalesPersons = async () => {
      try {
        setLoading(true);
        if (salesManagerId > 0) {
          const response = await axiosInstance.get(`/api/public/employee/getEmployeesUnderManager/${salesManagerId}`);
          setSalesPersons(response.data);
        } else {
          setSalesPersons([]);
        }
      } catch (error) {
        console.error("There was an error fetching the sales persons!", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSalesPersons();
  }, [salesManagerId, managers]);

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/public/contract/employeeContracts`, {
        params: {
          salesManagerId: salesManagerId || 0,
          salesPersonId: salesPersonId || 0,
        },
      });

      setContracts(response.data);
    } catch (error) {
      console.log(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContracts();
  }, [salesManagerId, salesPersonId]);

  const openApprovalModal = (communityId) => {
    setSelectedCommunityId(communityId);
    setShowApprovalModal(true);
  };

  const closeModal = () => {
    setShowApprovalModal(false);
  };

  const updateStatus = async (communityId, isActive) => {
    setShowApprovalModal(false);
    setLoading(true);
    try {
      const response = await axiosInstance.put(`/api/public/contract/updateActiveStatus`, null, {
        params: {
          clientId: communityId,
          activeStatus: !isActive,
        },
      });
      fetchContracts();
      setAlert({ visible: true, message: 'Status updated successfully!', type: 'success' });
    } catch (error) {
      setAlert({ visible: true, message: 'Error updating status: Please try again.', type: 'error' });
      console.error("Error updating status:", error);
    } finally {
      setLoading(false);
      setShowApprovalModal(false);
    }
  };

  const getStatusText = (status) => {
    if (status === null) {
      return "CLIENT_PENDING";
    }
    return status;
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "CLIENT_PENDING":
        return "bg-yellow-500 text-white";
      case "APPROVAL_PENDING":
        return "bg-orange-500 text-white";
      case "APPROVED":
        return "bg-green-500 text-white";
      default:
        return "";
    }
  };

  const handleSort = () => {
    const sortedRolePages = [...contracts].sort((a, b) => {
      const pageNameA = a.businessName.toLowerCase();
      const pageNameB = b.businessName.toLowerCase();

      if (sortOrder === 'asc') {
        return pageNameA.localeCompare(pageNameB);
      } else {
        return pageNameB.localeCompare(pageNameA);
      }
    });

    setContracts(sortedRolePages);
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(0);
  };

  const filteredContracts = contracts.filter(contract =>
    contract.businessName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contract.userFirstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contract.mobileNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (contract.annualRenewalFee && contract.annualRenewalFee.toString().includes(searchQuery))
  );

  const pageCount = Math.ceil(filteredContracts.length / itemsPerPage);
  const filteredData = filteredContracts.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

  const handleReinvite = async (emailId, firstName, contractCode) => {
    setLoading(true);
    fetchContracts();
    const emailInvitationData = {
      clientEmail: emailId,
      clientName: firstName,
      contractCode: contractCode,
    };

    try {
      const response = await axiosInstance.post(
        `/api/public/emailInvitation/send-invitation-To-The-First-Client`,
        emailInvitationData
      );
      setAlert({ visible: true, message: 'Invitation sent successfully!', type: 'success' });
    } catch (error) {
      console.error('Error sending invitation:', error);
      setAlert({ visible: true, message: 'Failed to send invitation: Please try again later.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Approval modal footer with action buttons
  const approvalModalFooter = (
    <div className="flex justify-end space-x-2">
      <button
        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        onClick={closeModal}
      >
        Cancel
      </button>
      <button
        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
        onClick={() => updateStatus(selectedCommunityId)}
      >
        Approve
      </button>
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen md:p-4">
      {loading && <Loading />}
      {!loading && (
        <div className="max-w-7xl mx-auto">
          {alert.visible && (
            <AlertMsg
              message={alert.message}
              type={alert.type}
              onClose={() => setAlert({ ...alert, visible: false })}
            />
          )}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Contract List</h1>
            {(roleId === 1004 || roleId === 1005) && (
              <Link to="/contract-generations" className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center gap-2">
                <Plus size={18} />
                <span>Add Contract</span>
              </Link>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div className="p-4 flex flex-col lg:flex-row items-start gap-2 md:gap-4">
              <div className="relative w-full lg:w-80 mb-0 md:mb-4 lg:mb-0">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search Contract..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </div>

              <div className="grid grid-cols-1 md:w-[500px] sm:grid-cols-2 gap-2 w-full">
                {roleId === 1000 && (
                  <select
                    id="salesManager"
                    value={salesManagerId}
                    onChange={(e) => handleManagerChange(Number(e.target.value))}
                    className="px-3 py-[11px] rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={0}>Select a manager</option>
                    {managers.sort((a, b) => a.firstName.localeCompare(b.firstName)).map((manager) => (
                      <option key={manager.employeeId} value={manager.employeeId}>
                        {manager.firstName}
                      </option>
                    ))}
                  </select>
                )}

                {(roleId === 1000 || roleId === 1004) && (
                  <select
                    id="salesPerson"
                    value={salesPersonId}
                    onChange={(e) => setSalesPersonId(Number(e.target.value))}
                    className="px-3 py-[11px] rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={0}>Select a sales person</option>
                    {salesManagerId > 0 && (
                      <option value={salesManagerId}>{salesManagerName}</option>
                    )}
                    {salesPersons.sort((a, b) => a.firstName.localeCompare(b.firstName)).map(person => (
                      <option key={person.employeeId} value={person.employeeId}>
                        {person.firstName}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-400">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider flex items-center whitespace-nowrap">
                      Business Name
                      <button className="inline-flex ml-1" onClick={handleSort} aria-label="Sort">
                        <AiOutlineSortAscending className="h-5 w-5" />
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Name</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Phone Number</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Renewal Fee</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Status</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Edit</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">No Data Found</td>
                    </tr>
                  ) : (
                    filteredData.map((contract) => (
                      <tr key={contract.contractid} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{contract.businessName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{contract.userFirstName} {contract.userLastName}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{contract.mobileNumber}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{contract.annualRenewalFee}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusClass(
                            getStatusText(contract.contractActiveStatus)
                          )}`}>
                            {getStatusText(contract.contractActiveStatus)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <Link to={`/edit-contract/${contract.contractId}`} className="text-gray-500 hover:text-gray-700">
                            <Pencil size={18} />
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex gap-3">
                            {contract.contractActiveStatus !== null && contract.contractActiveStatus !== 'APPROVED' && (
                              <button
                                onClick={() => openApprovalModal(contract.clientId)}
                                className="text-gray-500 hover:text-gray-700"
                                title="Approve Contract"
                              >
                                <AlertCircle size={18} />
                              </button>
                            )}
                            {contract.contractActiveStatus !== 'APPROVED' && contract.contractActiveStatus !== 'APPROVAL_PENDING' && (
                              <button
                                onClick={() => handleReinvite(contract.emailId, contract.userFirstName, contract.contractCode)}
                                className="text-gray-500 hover:text-gray-700"
                                title="Resend Email"
                              >
                                <Mail size={18} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Page {currentPage + 1} of {pageCount}
              </div>
              <div className="flex-1 flex justify-end">
                <Pagination
                  currentPage={currentPage}
                  pageCount={pageCount}
                  setCurrentPage={setCurrentPage}
                />
              </div>
            </div>
          </div>

          {/* Approval Modal using GlassmorphicModal */}
          <GlassmorphicModal
            isOpen={showApprovalModal}
            onClose={closeModal}
            title="Community Approval"
            size="md"
            footer={approvalModalFooter}
          >
            <p className="text-gray-700 mb-4">
              Are you sure you want to approve this community? This action will change the status of the contract.
            </p>
          </GlassmorphicModal>
        </div>
      )}
    </div>
  );
};

export default ContractList;